/**
 * Раскладка суточного графика «сутки по часам»: две дорожки на ОДНОЙ оси часов.
 *
 * Вынесено из компонента отдельным модулем не ради красоты: почти все ошибки
 * этого графика были арифметическими (ряд начинался с середины, пропуски
 * выглядели обрывом линии, столбик уезжал за подложку), а проверять арифметику
 * через рендер SVG — самый дорогой способ.
 *
 * Единицы координат здесь — CSS-пиксели, а не абстрактные единицы viewBox.
 * Причина конкретная: прошлая версия растягивала viewBox через
 * `preserveAspectRatio="none"`, и любая подпись внутри SVG поехала бы вместе с
 * ним — поэтому подписей у графика просто не было. Компонент измеряет свою
 * ширину и рисует 1:1, так что текст можно ставить прямо в SVG.
 */

/** Один час суток: обе величины и разбивка инцидентов по видам. */
export interface HourTrackPoint {
  /** Ярлык часа UTC, 0..23. */
  hour: number
  /** Доля часа в игре, %. `null` — данных за час нет (это не ноль). */
  uptimePct: number | null
  /** Сколько инцидентов. Всегда число: 0 значит «не было», а не «неизвестно». */
  incidents: number
  /** Из чего состояли инциденты, от частых к редким. */
  kinds: IncidentKindCount[]
}

export interface IncidentKindCount {
  kind: string
  count: number
}

/** Геометрия дорожек в пикселях. Задаётся компонентом, считается здесь. */
export interface TracksLayout {
  width: number
  padLeft: number
  padRight: number
  uptimeTop: number
  uptimeHeight: number
  barsTop: number
  barsHeight: number
}

export interface HourBand {
  index: number
  /** Левая кромка полосы часа. */
  x: number
  /** Центр полосы — к нему привязаны точка линии и подпись оси. */
  center: number
  width: number
}

export interface TracksGeometry {
  bands: HourBand[]
  /** Куски линии uptime: каждый — непрерывная серия известных часов. */
  runs: Array<{ line: string; area: string }>
  /** Известный час, окружённый пропусками: линию из одной точки не построить. */
  dots: Array<{ x: number; y: number }>
  /** Полосы «нет данных»: подряд идущие пропуски, слитые в одну зону. */
  gaps: Array<{ x: number; width: number; fromHour: number; toHour: number; hours: number }>
  bars: Array<{ index: number; x: number; y: number; width: number; height: number }>
  /** Верх шкалы инцидентов — по нему подписана ось. */
  incidentsTop: number
}

/** Полосы часов. Ширина полосы, а не «точка на линии»: столбик занимает полосу. */
export function bandsOf(count: number, layout: TracksLayout): HourBand[] {
  const plot = Math.max(0, layout.width - layout.padLeft - layout.padRight)
  const step = count === 0 ? 0 : plot / count
  return Array.from({ length: count }, (_unused, index) => {
    const x = layout.padLeft + index * step
    return { index, x, center: x + step / 2, width: step }
  })
}

/** Uptime → координата Y. Шкала всегда 0..100: процент не растягиваем по данным. */
export function uptimeY(value: number, layout: TracksLayout): number {
  const clamped = Math.max(0, Math.min(100, value))
  return layout.uptimeTop + layout.uptimeHeight - (clamped / 100) * layout.uptimeHeight
}

/**
 * Верх шкалы инцидентов.
 *
 * Считается по данным (не 100, как у процентов): при максимуме 2 инцидента шкала
 * до десяти превратила бы столбики в невидимые риски. Минимум 1 — чтобы пустые
 * сутки не делили на ноль и ось всё равно была подписана.
 */
export function incidentsTopOf(points: readonly HourTrackPoint[]): number {
  return Math.max(1, ...points.map(point => point.incidents))
}

export function buildTracks(
  points: readonly HourTrackPoint[],
  layout: TracksLayout
): TracksGeometry {
  const bands = bandsOf(points.length, layout)
  const incidentsTop = incidentsTopOf(points)
  const baseline = layout.uptimeTop + layout.uptimeHeight

  const runs: TracksGeometry['runs'] = []
  const dots: TracksGeometry['dots'] = []
  let current: Array<{ x: number; y: number }> = []

  const flush = (): void => {
    if (current.length === 1) {
      const only = current[0]
      if (only !== undefined) dots.push(only)
    } else if (current.length > 1) {
      const path = current.map(point => `${round(point.x)} ${round(point.y)}`).join(' L ')
      const first = current[0]
      const last = current[current.length - 1]
      if (first !== undefined && last !== undefined) {
        runs.push({
          line: `M ${path}`,
          // Площадь замыкается по базовой линии дорожки, а не по низу картинки:
          // иначе заливка залезала бы на дорожку инцидентов.
          area: `M ${round(first.x)} ${round(baseline)} L ${path} L ${round(last.x)} ${round(baseline)} Z`
        })
      }
    }
    current = []
  }

  points.forEach((point, index) => {
    const band = bands[index]
    if (band === undefined) return
    if (point.uptimePct === null) {
      flush()
      return
    }
    current.push({ x: band.center, y: uptimeY(point.uptimePct, layout) })
  })
  flush()

  return {
    bands,
    runs,
    dots,
    gaps: gapsOf(points, bands),
    bars: barsOf(points, bands, layout, incidentsTop),
    incidentsTop
  }
}

/**
 * Зоны «нет данных» — соседние пропуски сливаются в одну.
 *
 * Ради этого график и переделан: раньше пропуск просто рвал линию, и половина
 * ширины выглядела как «график сломался». Пропуск — это факт («статистику ещё не
 * собрали»), и он обязан быть нарисован и подписан.
 */
export function gapsOf(
  points: readonly HourTrackPoint[],
  bands: readonly HourBand[]
): TracksGeometry['gaps'] {
  const gaps: TracksGeometry['gaps'] = []
  let start: number | null = null

  const close = (endIndex: number): void => {
    if (start === null) return
    const from = bands[start]
    const to = bands[endIndex]
    const fromHour = points[start]?.hour
    const toHour = points[endIndex]?.hour
    if (from !== undefined && to !== undefined && fromHour !== undefined && toHour !== undefined) {
      gaps.push({
        x: from.x,
        width: to.x + to.width - from.x,
        fromHour,
        toHour,
        hours: endIndex - start + 1
      })
    }
    start = null
  }

  points.forEach((point, index) => {
    if (point.uptimePct === null) {
      start ??= index
      return
    }
    close(index - 1)
  })
  close(points.length - 1)
  return gaps
}

function barsOf(
  points: readonly HourTrackPoint[],
  bands: readonly HourBand[],
  layout: TracksLayout,
  top: number
): TracksGeometry['bars'] {
  const baseline = layout.barsTop + layout.barsHeight
  return points.flatMap((point, index) => {
    const band = bands[index]
    if (band === undefined || point.incidents <= 0) return []
    // Столбик уже полосы часа: зазор отделяет соседей, иначе 24 столбика
    // читаются как непрерывная заливка. И не тоньше 2px — иначе исчезает.
    const width = Math.max(2, band.width - 3)
    // Ненулевое значение обязано быть видно: 1 инцидент из 30 даёт долю 3%, что
    // на дорожке 40px — меньше пикселя.
    const height = Math.max(3, (point.incidents / top) * layout.barsHeight)
    return [
      {
        index,
        x: band.x + (band.width - width) / 2,
        y: baseline - height,
        width,
        height
      }
    ]
  })
}

/**
 * Шаг подписей оси часов.
 *
 * 24 подписи не влезают никогда, а «каждая третья» на мобиле превращается в
 * кашу. Считаем по доступной ширине из ширины подписи (11px mono, 2 знака ≈
 * 16px) с зазором.
 */
export function tickStepOf(plotWidth: number, count = 24): number {
  if (plotWidth <= 0) return 6
  const fits = Math.max(1, Math.floor(plotWidth / 30))
  for (const step of [1, 2, 3, 4, 6, 8, 12]) {
    if (Math.ceil(count / step) <= fits) return step
  }
  return 12
}

/** `14:00` — час UTC в подписи и тултипе. */
export function hourLabel(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`
}

/** `99.9%` либо честное «нет данных». */
export function uptimeLabel(value: number | null): string {
  return value === null ? 'нет данных' : `${value.toFixed(1)}%`
}

/** `3 инцидента`, `1 инцидент`, `нет инцидентов` — без «инцидентов: 1». */
export function incidentsLabel(count: number): string {
  if (count === 0) return 'нет инцидентов'
  const tail = count % 100
  const last = count % 10
  const word =
    tail >= 11 && tail <= 14
      ? 'инцидентов'
      : last === 1
        ? 'инцидент'
        : last < 5
          ? 'инцидента'
          : 'инцидентов'
  return `${count} ${word}`
}

/**
 * Виды инцидентов строкой: `отключения 2 · ошибки 1`.
 *
 * Обрезаем список: в тултипе больше трёх строк никто не читает, а «и ещё 2»
 * честно сообщает, что список неполный.
 */
export function kindsLabel(
  kinds: readonly IncidentKindCount[],
  formatKind: (kind: string) => string,
  limit = 3
): string {
  if (kinds.length === 0) return ''
  const shown = kinds.slice(0, limit).map(entry => `${formatKind(entry.kind)} ${entry.count}`)
  const hidden = kinds.length - shown.length
  return hidden > 0 ? `${shown.join(' · ')} · и ещё ${hidden}` : shown.join(' · ')
}

/** Округление до 0.1px: длинные хвосты в `d` раздувают разметку без пользы. */
function round(value: number): number {
  return Math.round(value * 10) / 10
}
